import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Floating popover for renaming an AAI speaker letter (A, B, …) to a
 * real name. Positions itself just under the anchor element using
 * fixed coords so a single popover can serve every "Speaker A" header
 * in the chat-log without duplicating per turn.
 *
 * Props:
 *   letter         — "A" | "B" | ...
 *   currentName    — current display name (or null if unmapped)
 *   personEntities — string[] of detected person names
 *   anchorEl       — HTMLElement the popover should float beneath
 *   onSet(name)    — set the mapping (empty string clears it)
 *   onClose()      — dismiss the popover
 */
export default function SpeakerLabelPopover({
  letter, currentName, personEntities, anchorEl, onSet, onClose,
}) {
  const [custom, setCustom] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const popRef = useRef(null);

  useLayoutEffect(() => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left });
  }, [anchorEl]);

  useEffect(() => {
    function handleClickOutside(e) {
      const inside = popRef.current?.contains(e.target);
      const onAnchor = anchorEl?.contains?.(e.target);
      if (!inside && !onAnchor) onClose();
    }
    function handleEsc(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose, anchorEl]);

  const uniqueNames = Array.from(new Set((personEntities || []).filter(Boolean)));

  function applyCustom() {
    const trimmed = custom.trim();
    if (trimmed) onSet(trimmed);
  }

  return (
    <div
      className="speaker-pop"
      ref={popRef}
      style={{ position: 'fixed', top: pos.top, left: pos.left }}
    >
      <div className="speaker-pop-title">
        Rename Speaker {letter}
        {currentName && (
          <span className="speaker-pop-current"> · currently <em>{currentName}</em></span>
        )}
      </div>

      {uniqueNames.length > 0 && (
        <>
          <div className="speaker-pop-section-label">Detected names</div>
          <div className="speaker-pop-options">
            {uniqueNames.map(name => (
              <button
                key={name}
                className={`speaker-pop-opt ${name === currentName ? 'speaker-pop-opt--active' : ''}`}
                onClick={() => onSet(name)}
                type="button"
              >
                {name}
              </button>
            ))}
          </div>
        </>
      )}

      <div className="speaker-pop-section-label">Custom name</div>
      <div className="speaker-pop-custom">
        <input
          type="text"
          value={custom}
          onChange={e => setCustom(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') applyCustom(); }}
          placeholder="Type a name…"
        />
        <button className="btn btn-sm btn-primary" onClick={applyCustom} type="button" disabled={!custom.trim()}>
          Set
        </button>
      </div>

      {currentName && (
        <div className="speaker-pop-actions">
          <button className="btn btn-sm" onClick={() => onSet('')} type="button">
            Reset to Speaker {letter}
          </button>
        </div>
      )}
    </div>
  );
}
